import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { ArrowLeft } from "lucide-react";
import { useApp } from "@/lib/store";

export default function AboutUsPage() {
  const { t } = useApp();
  const navigate = useNavigate();
  const [restaurantInfo, setRestaurantInfo] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const loadInfo = async () => {
      try {
        const res = await api.get("/menu/restaurant-info");
        setRestaurantInfo(res.data);
      } catch (e) {
        console.error("Failed to load restaurant info:", e);
      } finally {
        setLoading(false);
      }
    };
    loadInfo();
  }, []);

  const info = restaurantInfo || {};

  return (
    <div className="min-h-full bg-[#0A0A0A] relative z-[1]">
      {/* Header */}
      <div className="bg-gradient-to-b from-[#121212] to-[#0A0A0A] border-b border-[#27272A]">
        <div className="max-w-4xl mx-auto px-4 py-8">
          <Button
            onClick={() => navigate(-1)}
            variant="ghost"
            className="mb-6 text-[#D4B483] hover:bg-[#1A1A1A]"
          >
            <ArrowLeft className="w-4 h-4 mr-2" /> {t("Back")}
          </Button>
          <span className="lux-eyebrow">{t("Bästa Biten")}</span>
          <h1 className="font-playfair text-4xl md:text-5xl text-[#F5F1E8] mt-4 mb-2 lux-title">
            {t("About Us")}
          </h1>
          <p className="text-[#D4D4D8] italic font-light">{t("Get to know the restaurant")}</p>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-4xl mx-auto px-4 py-12">
        {loading ? (
          <div className="flex items-center justify-center py-20">
            <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-[#D4B483]"></div>
          </div>
        ) : (
          <div className="space-y-8">
            {info.about_banner_image && (
              <div className="rounded-2xl overflow-hidden border border-[#27272A]">
                <img src={info.about_banner_image} alt="About banner" className="w-full h-52 md:h-72 object-cover" />
              </div>
            )}

            {/* Restaurant Name and Contact */}
            <div className="bg-[#121212] rounded-2xl border border-[#27272A] p-8">
              <h2 className="font-playfair text-2xl font-bold text-[#EDEDED] mb-6">
                {info.name || "Bästa Biten"}
              </h2>

              <div className="grid md:grid-cols-2 gap-8">
                {/* Contact Information */}
                <div className="space-y-4">
                  <div>
                    <p className="text-[#D4D4D8] text-sm font-semibold mb-1">
                      {t("Address label")}
                    </p>
                    <p className="text-[#EDEDED] text-lg">
                      {info.address || "Olofström"}
                    </p>
                  </div>

                  {info.phone && (
                    <div>
                      <p className="text-[#D4D4D8] text-sm font-semibold mb-1">
                        {t("Phone label")}
                      </p>
                      <a
                        href={`tel:${info.phone}`}
                        className="text-[#D4B483] text-lg hover:underline"
                      >
                        {info.phone}
                      </a>
                    </div>
                  )}

                  <div>
                    <p className="text-[#D4D4D8] text-sm font-semibold mb-1">
                      {t("Opening Hours")}
                    </p>
                    <div className="space-y-1.5 text-sm text-[#EDEDED]">
                      {info.opening_hours ? (
                        Object.entries(info.opening_hours).map(([day, hours]) => (
                          <div key={day} className="flex justify-between">
                            <span className="capitalize">
                              {day.charAt(0).toUpperCase() + day.slice(1)}
                            </span>
                            <span className="text-[#D4D4D8]">
                              {hours.closed
                                ? t("Closed")
                                : `${hours.open} - ${hours.close}`}
                            </span>
                          </div>
                        ))
                      ) : (
                        <p className="text-[#D4D4D8]">{t("Opening hours not yet set")}</p>
                      )}
                    </div>
                  </div>
                </div>

                {/* Services */}
                <div className="space-y-4">
                  <h3 className="text-[#EDEDED] font-semibold mb-4">{t("Services")}</h3>
                  <div className="space-y-3">
                    <div className="flex items-start gap-3">
                      <div className="w-5 h-5 rounded-full bg-[#D4B483]/20 flex items-center justify-center mt-1 flex-shrink-0">
                        <div className="w-2 h-2 rounded-full bg-[#D4B483]"></div>
                      </div>
                      <div>
                        <p className="text-[#EDEDED] font-medium">
                          {info.accepting_pickup !== false ? t("Pickup service") : ""}
                        </p>
                        {info.accepting_pickup !== false && (
                          <p className="text-[#D4D4D8] text-sm">
                            {t("Pick up your food at the restaurant")}
                          </p>
                        )}
                      </div>
                    </div>

                    {info.accepting_delivery !== false && (
                      <div className="flex items-start gap-3">
                        <div className="w-5 h-5 rounded-full bg-[#D4B483]/20 flex items-center justify-center mt-1 flex-shrink-0">
                          <div className="w-2 h-2 rounded-full bg-[#D4B483]"></div>
                        </div>
                        <div>
                          <p className="text-[#EDEDED] font-medium">{t("Delivery service")}</p>
                          <p className="text-[#D4D4D8] text-sm">
                            {t("We deliver food to your home")}
                          </p>
                        </div>
                      </div>
                    )}

                  </div>
                </div>
              </div>
            </div>

            {/* About Description */}
            {info.about_us && (
              <div className="bg-[#121212] rounded-2xl border border-[#27272A] p-8">
                <h3 className="font-playfair text-2xl font-bold text-[#EDEDED] mb-4">
                  {t("About the restaurant")}
                </h3>
                <p className="text-[#EDEDED] leading-relaxed whitespace-pre-line">
                  {info.about_us}
                </p>
              </div>
            )}

            {/* Delivery Info */}
            {info.accepting_delivery !== false && (
              <div className="bg-[#121212] rounded-2xl border border-[#27272A] p-8">
                <h3 className="font-playfair text-xl font-bold text-[#EDEDED] mb-4">
                  {t("Delivery Info")}
                </h3>
                <div className="grid md:grid-cols-2 gap-6">
                  {info.delivery_fee !== undefined && (
                    <div>
                      <p className="text-[#D4D4D8] text-sm font-semibold mb-2">
                        {t("Delivery fee label")}
                      </p>
                      <p className="text-[#EDEDED] text-lg">
                        {info.delivery_fee} kr
                      </p>
                    </div>
                  )}
                  <div>
                    <p className="text-[#D4D4D8] text-sm font-semibold mb-2">
                      {t("Delivery area")}
                    </p>
                    <p className="text-[#EDEDED] text-lg">
                      {t("Up to")} {Math.round(info.max_delivery_drive_minutes || 7)} {t("min away")}
                    </p>
                  </div>
                </div>
              </div>
            )}

            {/* Current Announcement */}
            {info.enable_announcements && (
              <div className="bg-[#D4B483]/10 border border-[#D4B483]/30 rounded-2xl p-8">
                <div className="flex gap-4">
                  <div className="w-2 h-2 rounded-full bg-[#D4B483] mt-2 flex-shrink-0"></div>
                  <div>
                    <h3 className="text-[#EDEDED] font-semibold mb-2">
                      {t("Announcement")}
                    </h3>
                    <p className="text-[#EDEDED] leading-relaxed">
                      {info.current_announcement}
                    </p>
                  </div>
                </div>
              </div>
            )}

            {Array.isArray(info.site_gallery_images) && info.site_gallery_images.length > 0 && (
              <div className="bg-[#121212] rounded-2xl border border-[#27272A] p-8">
                <h3 className="font-playfair text-xl font-bold text-[#EDEDED] mb-4">
                  {t("Gallery")}
                </h3>
                <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                  {info.site_gallery_images.map((url, idx) => (
                    <img
                      key={`${url}-${idx}`}
                      src={url}
                      alt={`Galleri ${idx + 1}`}
                      className="w-full h-28 md:h-36 object-cover rounded-lg border border-[#27272A]"
                    />
                  ))}
                </div>
              </div>
            )}

            {/* Legal Information Links */}
            <div className="bg-[#121212] rounded-2xl border border-[#27272A] p-8">
              <h3 className="font-playfair text-xl font-bold text-[#EDEDED] mb-4">
                {t("Terms & Privacy")}
              </h3>
              <div className="flex flex-col gap-3">
                <a href="/privacy-policy" className="text-[#D4B483] hover:underline">
                  {t("Privacy Policy")}
                </a>
                <a href="/terms-of-service" className="text-[#D4B483] hover:underline">
                  {t("Terms of Service")}
                </a>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
